import React, { useEffect, useState, useCallback } from "react";
import { api, formatCOP } from "@/lib/api";
import PDFExportButton from "@/components/PDFExportButton";
import { cacheCatalog, getCachedCatalog, getCatalogCacheMeta } from "@/lib/offlineCache";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserPlus, Search, Edit2, MapPin, Phone, Mail, Archive, ShoppingBag, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import { useHasPerm, useAuth } from "@/context/AuthContext";
import { useSuppressCritical } from "@/context/NotifSuppressContext";
import { DEPARTAMENTOS_COLOMBIA } from "@/lib/departamentos";
import FileInput from "@/components/FileInput";
import ImportClientesWizard from "@/components/ImportClientesWizard";

const SEGMENTOS = [
  { key: "minorista", label: "Minorista", color: "bg-muted text-foreground/80" },
  { key: "mayorista", label: "Mayorista", color: "bg-emerald-100 text-primary" },
  { key: "vip", label: "VIP", color: "bg-amber-100 text-amber-800" },
];

const empty = { nombre: "", nombre_comercial: "", telefono: "", telefono_alterno: "", email: "", tipo_documento: "cc", numero_documento: "", cedula: "", nit: "", ciudad: "", departamento: "", zona: "", direccion: "", barrio: "", segmento: "minorista", lista_precios_id: "", vendedor_asignado_id: "", notas: "", capacidad_endeudamiento: "" };

export default function ClientesPage() {
  const canCreate = useHasPerm("clientes.crear");
  const canEdit = useHasPerm("clientes.editar");
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [listas, setListas] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [departamentosCliente, setDepartamentosCliente] = useState([]);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("");
  const [departamentoFilter, setDepartamentoFilter] = useState("");
  const [vendedorFilter, setVendedorFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailOrdenTop, setDetailOrdenTop] = useState("valor");
  const [importOpen, setImportOpen] = useState(false);

  // Suprimir modal crítico durante creación/edición de cliente o importación masiva
  useSuppressCritical(open || importOpen);

  const [offlineMeta, setOfflineMeta] = useState(null); // meta si estamos usando cache

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (seg) params.set("segmento", seg);
    if (departamentoFilter) params.set("departamento", departamentoFilter);
    if (vendedorFilter) params.set("vendedor_id", vendedorFilter);
    try {
      const [c, l, d, t] = await Promise.all([
        api.get(`/clientes?${params.toString()}`),
        api.get("/listas-precios"),
        api.get("/clientes/_/departamentos"),
        api.get("/team/lite").catch(() => ({ data: [] })),
      ]);
      setItems(c.data);
      setListas(l.data);
      setDepartamentosCliente(d.data);
      setVendedores((t.data || []).filter((u) => u.active !== false));
      setOfflineMeta(null);
      // Cachear catálogo para modo offline (solo cuando no hay filtros activos)
      if (!q && !seg && !departamentoFilter && !vendedorFilter) cacheCatalog("clientes", c.data);
    } catch (e) {
      // Fallback offline: si no hay red y hay cache, mostrarlo
      const cached = getCachedCatalog("clientes");
      if (cached && cached.length > 0) {
        setItems(cached);
        setOfflineMeta(getCatalogCacheMeta("clientes"));
      } else {
        throw e;
      }
    }
  }, [q, seg, departamentoFilter, vendedorFilter]);

  useEffect(() => { load(); }, [load]);

  // Refetch cuando el director cambia el nombre del usuario — el snapshot
  // `vendedor_asignado_nombre` de los clientes propios cambió (mar/2026)
  useEffect(() => {
    const onNameChange = () => load();
    window.addEventListener("user-name-changed", onNameChange);
    return () => window.removeEventListener("user-name-changed", onNameChange);
  }, [load]);

  const [rutFile, setRutFile] = useState(null); // solo al crear cliente
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      // Feb/2026: al crear cliente exigimos datos que el operador necesita
      // sí o sí para poder despachar. En edición no re-validamos para poder
      // arreglar clientes viejos incompletos.
      if (!editing) {
        const nombre = (payload.nombre || "").trim();
        const telefono = (payload.telefono || "").trim();
        const numDoc = (payload.numero_documento || "").trim();
        const direccion = (payload.direccion || "").trim();
        const faltantes = [];
        if (!nombre) faltantes.push("Nombre");
        if (!numDoc) faltantes.push(payload.tipo_documento === "nit" ? "NIT" : "Cédula");
        if (!telefono) faltantes.push("Teléfono principal");
        if (!direccion) faltantes.push("Dirección");
        if (faltantes.length > 0) {
          toast.error(
            `Falta: ${faltantes.join(", ")}. Estos datos son obligatorios para que el operador pueda despachar.`
          );
          return;
        }
      }
      if (!payload.lista_precios_id) payload.lista_precios_id = null;
      if (!payload.vendedor_asignado_id) payload.vendedor_asignado_id = null;
      // Cupo: acepta decimales. String vacío → null (sin límite).
      if (payload.capacidad_endeudamiento === "" || payload.capacidad_endeudamiento == null) {
        payload.capacidad_endeudamiento = null;
      } else {
        // permitir coma o punto como separador decimal, quitar separadores de miles
        const raw = String(payload.capacidad_endeudamiento).replace(/\./g, "").replace(",", ".");
        const n = parseFloat(raw);
        payload.capacidad_endeudamiento = isNaN(n) ? null : Math.max(0, n);
      }
      // Doc unificado: mandar tipo_documento + numero_documento. Backend
      // sincroniza cedula/nit automáticamente para retrocompat.
      payload.cedula = payload.tipo_documento === "cc" ? (payload.numero_documento || null) : null;
      payload.nit = payload.tipo_documento === "nit" ? (payload.numero_documento || null) : null;
      let newClienteId = null;
      if (editing) {
        await api.patch(`/clientes/${editing.id}`, payload);
        toast.success("Cliente actualizado");
        newClienteId = editing.id; // permitir subir/reemplazar RUT también al editar
      } else {
        const res = await api.post("/clientes", payload);
        newClienteId = res?.data?.id;
        toast.success("Cliente creado");
      }
      // Subir RUT si viene adjunto (crear o editar)
      if (rutFile && newClienteId) {
        try {
          const fd = new FormData();
          fd.append("file", rutFile);
          await api.post(`/clientes/${newClienteId}/rut`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("RUT cargado ✓");
        } catch (rutErr) {
          toast.error("Cliente creado pero no se pudo subir el RUT. Súbelo desde 'Ver detalle'.");
        }
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      setRutFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    }
  };

  const startEdit = async (c) => {
    // Traer detalle fresco (con flags rut_disponible / rut_ext + cupo enriquecido)
    let detalle = c;
    try {
      const { data } = await api.get(`/clientes/${c.id}`);
      detalle = data;
    } catch { /* si falla usamos el registro de la lista */ }
    setEditing(detalle);
    // Determinar tipo_documento y numero_documento a partir del cliente:
    // 1) si ya tiene el nuevo campo, se usa; 2) si tiene nit → NIT; 3) si tiene
    // cédula → CC; 4) sino intentar detectar por formato del documento_identidad.
    let _tipo = detalle.tipo_documento || "";
    let _num = detalle.numero_documento || "";
    if (!_tipo) {
      if (detalle.nit) { _tipo = "nit"; _num = detalle.nit; }
      else if (detalle.cedula) { _tipo = "cc"; _num = detalle.cedula; }
      else if (detalle.documento_identidad) {
        _tipo = /^\d{9,10}-?\d$/.test(detalle.documento_identidad) ? "nit" : "cc";
        _num = detalle.documento_identidad;
      } else { _tipo = "cc"; }
    }
    setForm({
      nombre: detalle.nombre || "", nombre_comercial: detalle.nombre_comercial || "", telefono: detalle.telefono || "", telefono_alterno: detalle.telefono_alterno || "", email: detalle.email || "",
      tipo_documento: _tipo, numero_documento: _num,
      cedula: detalle.cedula || "", nit: detalle.nit || "",
      ciudad: detalle.ciudad || "", departamento: detalle.departamento || "", zona: detalle.zona || "",
      direccion: detalle.direccion || "", barrio: detalle.barrio || "", segmento: detalle.segmento || "minorista",
      lista_precios_id: detalle.lista_precios_id || "", vendedor_asignado_id: detalle.vendedor_asignado_id || "",
      notas: detalle.notas || "",
      capacidad_endeudamiento: detalle.capacidad_endeudamiento == null ? "" : String(detalle.capacidad_endeudamiento),
    });
    setOpen(true);
  };

  const openDetail = async (c, orden = detailOrdenTop) => {
    const { data } = await api.get(`/clientes/${c.id}/historial?orden_top=${orden}`);
    setDetail(data);
  };

  // Al cambiar el toggle sin cerrar el modal, refrescar
  useEffect(() => {
    if (detail?.cliente?.id) {
      openDetail({ id: detail.cliente.id }, detailOrdenTop);
    }
    // eslint-disable-next-line
  }, [detailOrdenTop]);

  const archive = async (c) => {
    const msg = `¿Archivar el cliente "${c.nombre}"?\n\n` +
      `• El cliente ya no aparecerá en las listas.\n` +
      `• Sus pedidos existentes NO se borran.\n` +
      `• Puedes reactivarlo después desde el filtro "Archivados".`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/clientes/${c.id}`);
      toast.success("Cliente archivado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo archivar"); }
  };

  return (
    <div>
      {offlineMeta && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2" data-testid="offline-banner-clientes">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span><b>Sin conexión</b> — mostrando {offlineMeta.count} clientes guardados hace {offlineMeta.ageMinutes} min. Reconecta para ver la lista actualizada.</span>
        </div>
      )}
      <header className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">CRM · Fichas</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Clientes</h1>
          <p className="text-muted-foreground mt-2">
            {isDirector
              ? "Toda la relación comercial con los clientes del equipo en un solo lugar."
              : "Todos tus clientes asignados y su histórico comercial."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PDFExportButton
            endpoint="clientes"
            filename="beem-clientes"
            label="Exportar PDF"
            testid="export-clientes-pdf"
            fields={[
              { key: "vendedor_id", label: "Vendedor asignado", type: "select", optionsFrom: "vendedores" },
              { key: "ciudad", label: "Ciudad", type: "select", optionsFrom: "ciudades" },
              { key: "segmento", label: "Segmento", type: "select", optionsFrom: "segmentos" },
              { key: "solo_con_deuda", label: "Solo con deuda pendiente", type: "checkbox" },
            ]}
          />
          {canCreate && (
            <Button onClick={() => setImportOpen(true)} variant="outline" className="h-11" data-testid="import-clientes-btn">
              <Upload className="w-4 h-4 mr-2" /> Importar
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
              data-testid="new-cliente-btn"
              className="bg-primary hover:bg-primary/90 h-11 shadow-sm"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Nuevo cliente
            </Button>
          )}
        </div>
      </header>

      <UsageTip storageKey="clientes" steps={isDirector ? [
        "Asigna cada cliente a un <b>vendedor</b> y una <b>zona</b> desde el modal de edición.",
        "La <b>política comercial</b> (lista de precios) define el precio sugerido al armar un pedido.",
      ] : [
        "Aquí ves tus clientes asignados. Puedes filtrar por segmento o zona.",
        "Al armar un pedido para el cliente, se aplica automáticamente su <b>política comercial</b>.",
      ]} />

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
          <Input placeholder="Buscar por nombre, teléfono, ciudad..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" data-testid="search-cliente" />
        </div>
        <div className="flex gap-1 bg-card border border-border rounded-lg p-1">
          <button onClick={() => setSeg("")} className={`px-3 h-9 rounded-md text-sm ${!seg ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Todos</button>
          {SEGMENTOS.map((s) => (
            <button key={s.key} onClick={() => setSeg(s.key)} className={`px-3 h-9 rounded-md text-sm ${seg === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{s.label}</button>
          ))}
        </div>
        <select value={departamentoFilter} onChange={(e) => setDepartamentoFilter(e.target.value)} className="h-11 border border-border rounded-lg px-3 bg-card text-sm min-w-[180px]" data-testid="departamento-filter" title="Filtrar por departamento">
          <option value="">Todos los departamentos</option>
          {departamentosCliente.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={vendedorFilter} onChange={(e) => setVendedorFilter(e.target.value)} className="h-11 border border-border rounded-lg px-3 bg-card text-sm" data-testid="vendedor-filter" title="Filtrar por vendedor asignado">
          <option value="">Todos los vendedores</option>
          <option value="__none__">— Sin vendedor asignado —</option>
          {vendedores.filter((v) => v.role !== "operador").map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-card border border-border rounded-xl beem-table overflow-hidden">
        {/* Header: solo desktop */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-3">Cliente</div>
          <div className="col-span-2">Segmento</div>
          <div className="col-span-2">Ubicación</div>
          <div className="col-span-2">Vendedor</div>
          <div className="col-span-1 text-right">Compras</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {items.length === 0 && (
          <div className="px-6 py-16 text-center">
            <UserPlus className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <div className="mt-3 text-sm text-muted-foreground">
              {q || seg || departamentoFilter
                ? "Ningún cliente coincide con estos filtros."
                : "Aún no hay clientes. Crea el primero para empezar."}
            </div>
            {canCreate && (
              <Button
                onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
                className="mt-4 bg-primary hover:bg-primary/90"
                data-testid="new-cliente-empty-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Crear nuevo cliente
              </Button>
            )}
          </div>
        )}
        {items.map((c) => {
          const seg = SEGMENTOS.find((s) => s.key === c.segmento) || SEGMENTOS[0];
          return (
            <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-4 border-b border-border/60 items-start md:items-center hover:bg-muted/40 transition-colors" data-testid={`cliente-row-${c.id}`}>
              <div className="md:col-span-3 cursor-pointer min-w-0" onClick={() => openDetail(c)}>
                <div className="text-sm font-medium text-foreground break-words">{c.nombre}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                  {c.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefono}</span>}
                  {c.email && <span className="flex items-center gap-1 truncate max-w-[220px] md:max-w-none"><Mail className="w-3 h-3 flex-shrink-0" />{c.email}</span>}
                </div>
              </div>
              <div className="md:col-span-2"><span className={`text-xs px-2 py-1 rounded-full ${seg.color}`}>{seg.label}</span></div>
              <div className="md:col-span-2 text-xs text-foreground/80 min-w-0">
                <div className="flex items-center gap-1 truncate">{c.ciudad && <MapPin className="w-3 h-3 text-muted-foreground/70 flex-shrink-0" />}{c.ciudad || "—"}</div>
                {c.zona && <div className="text-muted-foreground truncate">{c.zona}</div>}
              </div>
              <div className="md:col-span-2 text-xs text-foreground/80 min-w-0">
                {c.vendedor_asignado_nombre ? (
                  <><div className="font-medium truncate">{c.vendedor_asignado_nombre}</div><div className="text-muted-foreground">asignado</div></>
                ) : (
                  <span className="text-muted-foreground/70">sin asignar</span>
                )}
              </div>
              <div className="md:col-span-1 text-left md:text-right flex md:block items-baseline gap-2">
                <div className="text-sm font-semibold text-foreground tabular-nums">{formatCOP(c.total_compras || 0)}</div>
                <div className="text-xs text-muted-foreground">· {c.pedidos_count || 0} ped.</div>
              </div>
              <div className="md:col-span-2 flex justify-start md:justify-end gap-1 flex-wrap">
                <button onClick={() => openDetail(c)} className="p-2 rounded-md hover:bg-pink-50 text-muted-foreground/70 hover:text-pink-600" title="Historial" data-testid={`hist-${c.id}`}>
                  <ShoppingBag className="w-4 h-4" />
                </button>
                {canEdit && (
                  <button onClick={() => startEdit(c)} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Editar" data-testid={`edit-cliente-${c.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                {canEdit && (
                  <button onClick={() => archive(c)} className="p-2 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600" title="Archivar">
                    <Archive className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit modal */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
        <DialogContent data-testid="cliente-modal" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar cliente" : "Nuevo cliente"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <Label>Nombre*</Label>
              <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="h-11 mt-1" data-testid="cliente-nombre" />
            </div>
            <div className="col-span-2">
              <Label>Nombre comercial</Label>
              <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} className="h-11 mt-1" placeholder="Ej: Boutique Colores S.A.S." data-testid="cliente-nombre-comercial" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Aparece en la remisión y en el rótulo del envío. Opcional.</p>
            </div>
            <div>
              <Label>Teléfono principal*</Label>
              <Input required={!editing} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-11 mt-1" placeholder="+57 300 000 0000" data-testid="cliente-telefono" />
            </div>
            <div>
              <Label>Teléfono alterno <span className="text-[10px] text-muted-foreground">(opcional)</span></Label>
              <Input value={form.telefono_alterno || ""} onChange={(e) => setForm({ ...form, telefono_alterno: e.target.value })} className="h-11 mt-1" placeholder="Ej: WhatsApp de la esposa" data-testid="cliente-telefono-alterno" />
            </div>
            <div>
              <Label>Correo</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Tipo de documento</Label>
              <div className="flex gap-2 mt-1">
                {[["cc", "Cédula (C.C.)"], ["nit", "NIT"]].map(([v, l]) => (
                  <label key={v}
                    className={`flex-1 h-11 flex items-center justify-center gap-2 rounded-lg border cursor-pointer text-sm font-medium transition ${form.tipo_documento === v ? "bg-primary/10 border-primary text-primary" : "bg-card border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`cliente-tipo-doc-${v}`}>
                    <input type="radio" name="cli-tipo-doc" className="accent-primary"
                      checked={form.tipo_documento === v}
                      onChange={() => setForm({ ...form, tipo_documento: v })} />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Número de {form.tipo_documento === "nit" ? "NIT" : "cédula"}*</Label>
              <Input
                required={!editing}
                value={form.numero_documento || ""}
                onChange={(e) => setForm({ ...form, numero_documento: e.target.value })}
                className="h-11 mt-1"
                placeholder={form.tipo_documento === "nit" ? "Ej: 900.123.456-7" : "Ej: 1.098.765.432"}
                data-testid="cliente-numero-doc"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">Solo un documento por cliente. Obligatorio: el operador lo necesita para el rótulo del envío.</p>
            </div>
            {!editing && (
              <div className="col-span-2">
                <Label>RUT (opcional)</Label>
                <FileInput
                  value={rutFile}
                  onChange={setRutFile}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  label="Adjuntar RUT"
                  testid="cliente-rut-create-input"
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  PDF, JPG o PNG. Se subirá automáticamente al crear el cliente.
                </p>
              </div>
            )}
            {editing && (
              <div className="col-span-2">
                <Label>RUT del cliente</Label>
                <div className="mt-1 flex items-center gap-2">
                  {editing.rut_disponible ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/clientes/${editing.id}/rut`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary font-semibold underline hover:no-underline"
                      data-testid="cliente-rut-ver-link"
                    >
                      📎 Ver RUT actual ({editing.rut_ext?.toUpperCase() || "archivo"})
                    </a>
                  ) : (
                    <span className="text-xs text-muted-foreground">Sin RUT cargado.</span>
                  )}
                </div>
                <FileInput
                  value={rutFile}
                  onChange={setRutFile}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  label={editing.rut_disponible ? "Reemplazar RUT" : "Adjuntar RUT"}
                  testid="cliente-rut-edit-input"
                  className="mt-2"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {editing.rut_disponible
                    ? "Sube un archivo nuevo para reemplazar el actual."
                    : "PDF, JPG o PNG. Se cargará al guardar."}
                </p>
              </div>
            )}
            <div className="col-span-2">
              <Label>Cupo de crédito (COP)</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.capacidad_endeudamiento === "" ? "" : Number(form.capacidad_endeudamiento).toLocaleString("es-CO")}
                onChange={(e) => setForm({ ...form, capacidad_endeudamiento: e.target.value.replace(/[^\d]/g, "") })}
                className="h-11 mt-1 tabular-nums"
                placeholder="Ej: 500.000 · Deja vacío para crédito sin límite"
                data-testid="cliente-cupo"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Monto máximo de deuda simultánea. Al confirmar un pedido nuevo, si la deuda total supera este valor el sistema bloquea el pedido. Vacío = sin límite. 0 = crédito bloqueado (siempre paga contra entrega).
              </p>
            </div>
            <div>
              <Label>Departamento</Label>
              <select
                value={form.departamento || ""}
                onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm"
                data-testid="cliente-departamento"
              >
                <option value="">Selecciona un departamento...</option>
                {DEPARTAMENTOS_COLOMBIA.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-0.5">Se usa para filtrar la descarga de "Preparar visita".</p>
            </div>
            <div>
              <Label>Pueblo / Referencia</Label>
              <Input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className="h-11 mt-1" placeholder="Ej: Rionegro, La Ceja, San Antonio de Prado..." data-testid="cliente-pueblo" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Nombre del pueblo o municipio como referencia para ubicar al cliente.</p>
            </div>
            <div className="col-span-2">
              <Label>Dirección*</Label>
              <Input required={!editing} value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className="h-11 mt-1" placeholder="Calle 45 # 67-89, Barrio Los Colores" data-testid="cliente-direccion" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Obligatoria: el operador la usa para el rótulo del envío.</p>
            </div>
            <div className="col-span-2">
              <Label>Barrio</Label>
              <Input value={form.barrio} onChange={(e) => setForm({ ...form, barrio: e.target.value })} className="h-11 mt-1" placeholder="Ej: Los Colores, El Poblado, Centro" data-testid="cliente-barrio" />
              <p className="text-[10px] text-muted-foreground mt-0.5">Sale en el rótulo del paquete junto a la dirección.</p>
            </div>
            <div>
              <Label>Segmento</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {SEGMENTOS.map((s) => (
                  <button key={s.key} type="button" onClick={() => setForm({ ...form, segmento: s.key })}
                    className={`h-11 rounded-md border text-[13px] font-medium leading-none whitespace-nowrap px-1 truncate flex items-center justify-center ${form.segmento === s.key ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}
                    data-testid={`seg-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Lista de precios (política comercial)</Label>
              <select value={form.lista_precios_id} onChange={(e) => setForm({ ...form, lista_precios_id: e.target.value })} className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm">
                <option value="">Precio base (sin descuento)</option>
                {listas.map((l) => <option key={l.id} value={l.id}>{l.nombre} · {l.descuento_pct}% desc</option>)}
              </select>
            </div>
            <div className="col-span-2">
              {isDirector ? (
                <>
                  <Label>Vendedor asignado</Label>
                  <select value={form.vendedor_asignado_id} onChange={(e) => setForm({ ...form, vendedor_asignado_id: e.target.value })} className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="cliente-vendedor">
                    <option value="">Sin asignar</option>
                    {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.email}</option>)}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">El vendedor asignado será el único (además de directores) que pueda ver este cliente y crearle pedidos.</p>
                </>
              ) : (
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm text-foreground">
                  <b>Vendedor asignado:</b> {editing ? (form.vendedor_asignado_id ? "asignado" : "sin asignar") : (user?.name || "tú")}
                  <div className="text-xs text-muted-foreground mt-1">
                    {editing
                      ? "Solo un director puede cambiar la asignación."
                      : "Este cliente quedará asignado a ti automáticamente."}
                  </div>
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Notas internas</Label>
              <textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[80px]" placeholder="Preferencias, contactos, condiciones especiales..." />
            </div>
            <div className="col-span-2 sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-background/95 backdrop-blur border-t border-border flex justify-end gap-2 z-10">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="cliente-save">{editing ? "Guardar cambios" : "Crear cliente"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail (historial) modal */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{detail.cliente.nombre}</DialogTitle>
                <p className="text-sm text-muted-foreground">{detail.cliente.ciudad || "Sin ciudad"} · {detail.cliente.segmento}</p>
              </DialogHeader>

              {/* Identificación + RUT */}
              <ClienteIdentificacion
                cliente={detail.cliente}
                canEdit={isDirector || (user?.permissions || []).includes("clientes.editar")}
                onChanged={() => openDetail({ id: detail.cliente.id }, detailOrdenTop)}
              />

              {/* Perfil comercial — visible para TODOS los que pueden ver el cliente (incluido operador) */}
              <ClientePerfil cliente={detail.cliente} listas={listas} />

              {/* KPIs y pedidos: solo visible para director/vendedor (quien ve compras/comisiones) */}
              {(canCreate || canEdit) && (
                <>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-muted/40 border border-border rounded-lg p-4">
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">Total compras</div>
                  <div className="text-2xl font-semibold tabular-nums mt-1">{formatCOP(detail.total_compras)}</div>
                </div>
                <div className="bg-muted/40 border border-border rounded-lg p-4">
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">Pedidos</div>
                  <div className="text-2xl font-semibold tabular-nums mt-1">{detail.pedidos_count}</div>
                </div>
                <div className="bg-muted/40 border border-border rounded-lg p-4">
                  <div className="text-[10px] font-bold uppercase text-muted-foreground">Ultimo</div>
                  <div className="text-sm font-medium mt-1">{detail.cliente.ultimo_pedido || "—"}</div>
                </div>
              </div>
              {detail.productos_top?.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Productos favoritos</div>
                    <div className="flex bg-muted/50 border border-border rounded-full p-0.5" title="Cambia el criterio de ordenamiento">
                      <button
                        onClick={() => setDetailOrdenTop("valor")}
                        data-testid="cli-orden-valor"
                        className={`px-2.5 h-6 rounded-full text-[9px] font-bold uppercase tracking-wider transition-colors ${detailOrdenTop === "valor" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        title="Ordenar por dinero"
                      >
                        $ Valor
                      </button>
                      <button
                        onClick={() => setDetailOrdenTop("unidades")}
                        data-testid="cli-orden-unidades"
                        className={`px-2.5 h-6 rounded-full text-[9px] font-bold uppercase tracking-wider transition-colors ${detailOrdenTop === "unidades" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        title="Ordenar por unidades"
                      >
                        # Unidades
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {detail.productos_top.map((p) => (
                      <div key={p.nombre} className="py-2 flex justify-between text-sm">
                        <span>{p.nombre}</span>
                        <span className="tabular-nums text-muted-foreground">{p.cantidad} u · {formatCOP(p.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-6">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Historial de pedidos</div>
                <div className="divide-y divide-slate-100">
                  {detail.pedidos.length === 0 && <div className="py-4 text-sm text-muted-foreground/70">Aun sin pedidos.</div>}
                  {detail.pedidos.map((p) => (
                    <div key={p.id} className={`py-3 flex justify-between text-sm ${p.estado === "borrador" ? "opacity-70" : ""}`} data-testid={`cliente-pedido-${p.id}`}>
                      <div>
                        <div className="font-medium text-foreground flex items-center gap-2">
                          {p.numero || "Sin número"}
                          {p.estado === "borrador" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold uppercase tracking-wide">Borrador</span>}
                          {p.estado === "cancelado" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold uppercase tracking-wide">Cancelado</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{p.fecha} · {p.estado}{p.vendedor_nombre ? ` · ${p.vendedor_nombre}` : ""}</div>
                      </div>
                      <div className={`tabular-nums font-semibold ${p.estado === "borrador" ? "text-muted-foreground" : ""}`}>{formatCOP(p.total)}</div>
                    </div>
                  ))}
                </div>
              </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import clients wizard */}
      <ImportClientesWizard
        open={importOpen}
        onOpenChange={setImportOpen}
        onSuccess={load}
      />

      {/* Floating Action Button (visible en móvil y siempre a la vista) */}
      {canCreate && (
        <button
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="fixed bottom-6 right-6 z-40 md:hidden w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-soft-lift hover:scale-105 active:scale-95 transition-transform flex items-center justify-center"
          data-testid="new-cliente-fab"
          aria-label="Nuevo cliente"
        >
          <UserPlus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}

// -------------------- Identificación + RUT --------------------
function ClienteIdentificacion({ cliente, canEdit, onChanged }) {  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();
  const tieneRut = !!cliente.rut;

  const rutUrl = `${api.defaults.baseURL}/clientes/${cliente.id}/rut`;

  const subir = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/clientes/${cliente.id}/rut`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("RUT cargado ✓");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir");
    } finally {
      setUploading(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm("¿Eliminar el RUT de este cliente?")) return;
    try {
      await api.delete(`/clientes/${cliente.id}/rut`);
      toast.success("RUT eliminado");
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const verRut = async () => {
    // Abrimos en pestaña nueva con blob para preservar la auth header
    try {
      const { data } = await api.get(`/clientes/${cliente.id}/rut`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error("No se pudo abrir el RUT");
    }
  };

  return (
    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 p-3 rounded-lg bg-muted/30 border border-border">
      <div className="text-sm space-y-1">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">Cédula</div>
          <div className="tabular-nums font-medium" data-testid="cliente-detalle-cedula">
            {cliente.cedula || (cliente.documento_identidad && !/^\d{9,10}-?\d$/.test(cliente.documento_identidad) ? cliente.documento_identidad : <span className="text-muted-foreground/60 italic font-normal">—</span>)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">NIT</div>
          <div className="tabular-nums font-medium" data-testid="cliente-detalle-nit">
            {cliente.nit || (cliente.documento_identidad && /^\d{9,10}-?\d$/.test(cliente.documento_identidad) ? cliente.documento_identidad : <span className="text-muted-foreground/60 italic font-normal">—</span>)}
          </div>
        </div>
      </div>
      <div className="text-sm">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-0.5">RUT (documento)</div>
        {tieneRut ? (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={verRut}
              className="text-primary underline text-sm font-medium hover:text-primary/80"
              data-testid="cliente-ver-rut"
            >
              📎 Ver {cliente.rut?.content_type?.includes("pdf") ? "PDF" : "imagen"}
            </button>
            <span className="text-[10px] text-muted-foreground">
              {cliente.rut?.filename || "archivo"} · {Math.round((cliente.rut?.size || 0) / 1024)} KB
            </span>
            {canEdit && (
              <>
                <button onClick={() => inputRef.current?.click()} className="text-[11px] text-muted-foreground underline hover:text-foreground">Reemplazar</button>
                <button onClick={eliminar} className="text-[11px] text-rose-600 underline hover:text-rose-700">Eliminar</button>
              </>
            )}
          </div>
        ) : canEdit ? (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
            data-testid="cliente-subir-rut"
          >
            {uploading ? "Subiendo..." : "📤 Subir RUT"}
          </button>
        ) : (
          <span className="text-muted-foreground/60 italic text-sm">Sin cargar</span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subir(f); }}
        />
      </div>
    </div>
  );
}


// -------------------- Perfil comercial (visible para todos con clientes.ver) --------------------
function ClientePerfil({ cliente, listas }) {
  const lista = (listas || []).find((l) => l.id === cliente.lista_precios_id);
  const segLabel = { minorista: "Minorista", mayorista: "Mayorista", vip: "VIP" }[cliente.segmento] || cliente.segmento;
  const segClass = {
    vip: "bg-amber-100 text-amber-800 border-amber-200",
    mayorista: "bg-purple-100 text-purple-800 border-purple-200",
    minorista: "bg-blue-100 text-blue-800 border-blue-200",
  }[cliente.segmento] || "bg-muted text-muted-foreground border-border";

  const rows = [
    { label: "Teléfono", value: cliente.telefono, testid: "perfil-tel" },
    { label: "Teléfono alterno", value: cliente.telefono_alterno, testid: "perfil-tel-alt" },
    { label: "Correo", value: cliente.email, testid: "perfil-email" },
    { label: "Dirección", value: cliente.direccion, testid: "perfil-dir" },
    { label: "Barrio", value: cliente.barrio, testid: "perfil-barrio" },
    { label: "Ciudad", value: cliente.ciudad, testid: "perfil-ciudad" },
    { label: "Departamento", value: cliente.departamento, testid: "perfil-dep" },
    { label: "Zona comercial", value: cliente.zona, testid: "perfil-zona" },
    { label: "Vendedor asignado", value: cliente.vendedor_asignado_nombre || (cliente.vendedor_asignado_id ? "Asignado" : "—"), testid: "perfil-vendedor" },
    { label: "Lista de precios", value: lista ? `${lista.nombre} · ${lista.descuento_pct}% desc` : "Precio base", testid: "perfil-lista" },
    { label: "Registrado", value: cliente.created_at ? new Date(cliente.created_at).toLocaleDateString("es-CO") : "—", testid: "perfil-created" },
  ];

  return (
    <div className="mt-4 p-4 rounded-lg bg-card border border-border" data-testid="cliente-perfil-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Perfil comercial</h3>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${segClass}`}>{segLabel}</span>
      </div>

      {/* Widget de cupo de crédito */}
      <CupoCreditoBadge cliente={cliente} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-3">
        {rows.map((r) => (
          <div key={r.label} className="flex justify-between gap-3 border-b border-border/40 pb-1">
            <span className="text-[11px] uppercase text-muted-foreground tracking-wider">{r.label}</span>
            <span className="text-right font-medium truncate" data-testid={r.testid} title={r.value || "—"}>
              {r.value || <span className="text-muted-foreground/60 italic font-normal">—</span>}
            </span>
          </div>
        ))}
      </div>
      {cliente.notas && (
        <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200">
          <div className="text-[10px] font-bold uppercase text-amber-800 mb-0.5">Notas internas</div>
          <div className="text-sm text-amber-900 whitespace-pre-wrap">{cliente.notas}</div>
        </div>
      )}
    </div>
  );
}

function CupoCreditoBadge({ cliente }) {
  const cupo = cliente.capacidad_endeudamiento;
  const deuda = cliente.deuda_pendiente || 0;
  const disponible = cliente.cupo_disponible;
  const excedido = cliente.cupo_excedido;

  // Sin límite configurado → info neutra
  if (cupo == null) {
    return (
      <div className="p-3 rounded-lg bg-muted/50 border border-border">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Crédito</div>
        <div className="text-sm">
          <span className="text-muted-foreground/70 italic">Sin cupo configurado</span>
          {deuda > 0 && <span className="ml-2 text-foreground">· Deuda actual: <b className="tabular-nums">{formatCOP(deuda)}</b></span>}
        </div>
      </div>
    );
  }

  // Cupo bloqueado (0)
  if (cupo === 0) {
    return (
      <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
        <div className="text-[10px] font-bold uppercase tracking-widest text-rose-700 mb-1">🔒 Crédito bloqueado</div>
        <div className="text-sm text-rose-800">Este cliente debe pagar contra entrega. No se acepta deuda.</div>
      </div>
    );
  }

  const pct = cupo > 0 ? Math.min(100, Math.round((deuda / cupo) * 100)) : 0;
  const barColor = excedido ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  const bgColor = excedido ? "bg-rose-50 border-rose-200" : pct >= 80 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200";
  const labelColor = excedido ? "text-rose-800" : pct >= 80 ? "text-amber-800" : "text-emerald-800";

  return (
    <div className={`p-3 rounded-lg border ${bgColor}`} data-testid="cupo-widget">
      <div className="flex items-baseline justify-between mb-1 gap-2 flex-wrap">
        <div className={`text-[10px] font-bold uppercase tracking-widest ${labelColor}`}>
          {excedido ? <><AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-1" />Cupo excedido</> : "Cupo de crédito"}
        </div>
        <div className="text-xs tabular-nums font-semibold">
          <span className={labelColor}>{formatCOP(deuda)}</span>
          <span className="text-muted-foreground"> / {formatCOP(cupo)}</span>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full bg-white/80 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, Math.max(pct, excedido ? 100 : 2))}%` }} />
      </div>
      <div className={`text-[10px] mt-1 ${labelColor}`}>
        {excedido
          ? `Excede en ${formatCOP(deuda - cupo)}. El vendedor no podrá confirmar más pedidos hasta cobrar pendientes.`
          : disponible > 0
            ? `Disponible: ${formatCOP(disponible)} (${100 - pct}% del cupo)`
            : "Cupo agotado. No hay margen para pedidos nuevos hasta cobrar."}
      </div>
    </div>
  );
}
